import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ElementRef, inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, map, Observable, take, tap } from 'rxjs';

import {
  DocumentForm,
  DocumentItem,
  DocumentLabels,
} from '@shared/components/upload/document-upload-file.interface';
import { FileValidators } from '@shared/validators/file.validators';

import { DocumentData } from '.';

@Injectable({ providedIn: 'root' })
export class DocumentUploadService {
  private readonly httpClient = inject(HttpClient);

  private readonly dialogRef = inject(DialogRef);

  private readonly data: DocumentData = inject(DIALOG_DATA);

  public title: string = this.data.title;

  public description: string = this.data.description;

  public labels: DocumentLabels = this.data.labels;

  public maxNumberOfDocuments: number = this.data.maxNumberOfDocuments;

  public token: string = this.data.token;

  public documents$$: BehaviorSubject<DocumentItem[]> = new BehaviorSubject<DocumentItem[]>(
    this.data.documents,
  );

  public documents$: Observable<DocumentItem[]> = this.documents$$.asObservable();

  public isProcessing$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public isProcessing$: Observable<boolean> = this.isProcessing$$.asObservable();

  public hasApiError$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public hasApiError$: Observable<boolean> = this.hasApiError$$.asObservable();

  public preview$$: BehaviorSubject<DocumentItem | undefined> = new BehaviorSubject<
    DocumentItem | undefined
  >(undefined);

  public preview$: Observable<DocumentItem | undefined> = this.preview$$.asObservable();

  public form: FormGroup<DocumentForm> = new FormGroup<DocumentForm>({
    document: new FormControl(null, [
      Validators.required,
      FileValidators.maxSize(5_242_880),
      FileValidators.allowedExtensions(['doc', 'docx', 'pdf', 'jpg', 'jpeg', 'png']),
      FileValidators.filenameMaxLength(45),
      FileValidators.filenamePattern(/^[^'";()|&<>*^]+$/),
    ]),
  });

  public hasDocuments = !!this.data.documents.length;

  public fileInput!: ElementRef;

  private readonly headers = {
    token: this.token,
  };

  constructor() {
    if (this.data.documents?.length) {
      this.select(this.data.documents[0]);
    }
  }

  public load(documentId: string): Observable<string> {
    this.startApiProcessing();

    const params: HttpParams = new HttpParams({
      fromObject: {
        encoded: true,
      },
    });
    return this.httpClient
      .get<{ content: string; type: string }>(
        `/rgg/inheritances/v1/inheritances/documents/${documentId}`,
        {
          headers: { ...this.headers },
          params,
        },
      )
      .pipe(
        tap(() => {
          this.isProcessing$$.next(false);
        }),
        map(({ content, type }) =>
          URL.createObjectURL(this.base64ToBlob(content, this.getContentType(type))),
        ),
      );
  }

  public upload(document: File): void {
    this.startApiProcessing();

    const params: HttpParams = new HttpParams({
      fromObject: {
        token: this.token,
        typology: this.data.documentType,
        attachmentGroup: this.data.attachmentGroup,
      },
    });

    const formData = new FormData();
    formData.append('file', document, document.name);
    this.httpClient
      .post<DocumentItem>('/rgg/inheritances/v1/inheritances/documents', formData, { params })
      .pipe(
        tap(() => {
          this.isProcessing$$.next(false);
        }),
        take(1),
      )
      .subscribe({
        next: (response: DocumentItem) => {
          const item: DocumentItem = {
            ...response,
            path: URL.createObjectURL(document),
          };
          this.preview$$.next(item);
          this.documents$$.next([...this.documents$$.getValue(), item]);
        },
        error: (error) => {
          this.logApiError(error);
        },
      });
  }

  public delete({ referenceId, path }: DocumentItem): void {
    this.startApiProcessing();

    this.httpClient
      .delete(`/rgg/inheritances/v1/inheritances/documents/${referenceId}`, {
        headers: { ...this.headers },
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.endApiProcessing();

          if (path) {
            URL.revokeObjectURL(path);
          }
          this.documents$$.next([
            ...this.documents$$
              .getValue()
              .filter((document: DocumentItem) => document.referenceId !== referenceId),
          ]);
          if (this.preview$$.getValue()?.referenceId === referenceId) {
            this.preview$$.next(this.documents$$.getValue()[0]);
          }
        },
        error: (error) => {
          this.logApiError(error);
        },
      });
  }

  public select(documentItem: DocumentItem): void {
    if (!documentItem.path) {
      this.load(documentItem.referenceId)
        .pipe(take(1))
        .subscribe({
          next: (url: string) => {
            const newDocument: DocumentItem = {
              ...documentItem,
              path: url,
            };
            this.documents$$.next([
              ...this.documents$$.getValue().map((document: DocumentItem) => {
                if (document.referenceId === documentItem.referenceId) {
                  return newDocument;
                }
                return document;
              }),
            ]);

            this.preview$$.next(newDocument);
          },
          error: (error) => {
            this.logApiError(error);
          },
        });
    } else {
      this.preview$$.next(documentItem);
    }
  }

  public newDocument(): void {
    this.preview$$.next(undefined);
  }

  public saveDocuments(): void {
    this.dialogRef.close(this.documents$$.getValue());
  }

  public base64ToBlob(b64Data: string, contentType = '', sliceSize = 512): Blob {
    const byteCharacters = window.atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  public registerFileInput(fileInput: ElementRef): void {
    this.fileInput = fileInput;
  }

  public getContentType(type: string): string {
    switch (type) {
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'pdf':
        return 'application/pdf';
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      default:
        return '';
    }
  }

  public startApiProcessing(): void {
    this.isProcessing$$.next(true);
    if (this.hasApiError$$.getValue()) {
      this.hasApiError$$.next(false);
    }
  }

  public endApiProcessing(): void {
    this.isProcessing$$.next(false);
  }

  public logApiError(error: unknown): void {
    console.error(error);
    this.hasApiError$$.next(true);
    this.isProcessing$$.next(false);
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
